import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  ThemeProvider,
  createTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  Autocomplete,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { extractList } from '../utils/response';
import { createTeam, getTeamById, updateTeam } from '../store/slices/teamsSlice';
import { getUsersByRole, getUserStatus } from '../store/slices/userSlice';
import { TEAMS_LISTS_PATH } from '../navigations/path';

// 🎨 Theme
const revivePayTheme = createTheme({
  palette: {
    primary: { main: '#0a3d62' },
    success: { main: '#4caf50' },
    error: { main: '#d32f2f' },
  },
});

const CreateTeamForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode'); // 'edit' | 'view' | null
  const teamId = queryParams.get('id');

  // Form State
  const [teamName, setTeamName] = useState('');
  const [teamLead, setTeamLead] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [status, setStatus] = useState('');

  // Data
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(mode === 'view');

  // Errors
  const [errors, setErrors] = useState({
    teamName: '',
    teamLead: '',
    status: '',
  });

  // === FETCH DATA ===
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [statusRes, leadsRes, usersRes] = await Promise.allSettled([
          dispatch(getUserStatus()).unwrap(),
          dispatch(getUsersByRole('Sales Lead')).unwrap(),
          dispatch(getUsersByRole('Sales User')).unwrap(),
        ]);

        const statusList = statusRes.status === 'fulfilled' ? statusRes.value : [];
        setStatuses(Array.isArray(statusList) ? statusList : statusList?.list || []);

        const leadsRaw = leadsRes.status === 'fulfilled' ? (leadsRes.value?.data ?? leadsRes.value ?? []) : [];
        const leadsList = extractList(leadsRaw);
        const normalizedLeads = leadsList.map(u => ({ ...u, uuid: u.uuid || u.id }));
        setLeads(normalizedLeads);

        const usersRaw = usersRes.status === 'fulfilled' ? (usersRes.value?.data ?? usersRes.value ?? []) : [];
        const usersList = extractList(usersRaw);
        const normalizedUsers = usersList.map(u => ({ ...u, uuid: u.uuid || u.id }));
        setUsers(normalizedUsers);

        if (teamId && (mode === 'edit' || mode === 'view')) {
          const teamRes = await dispatch(getTeamById(teamId)).unwrap();
          const team = teamRes?.data ?? teamRes;
          if (!team) throw new Error('Team not found');

          setTeamName(team.name || '');

          // Status
          const activeStatus = statuses.find(s => s.name.toLowerCase().includes('active'));
          setStatus(team.status?.id || activeStatus?.id || statuses[0]?.id || '');

          // Team Lead
          const leadMember = team.members?.find(m => m.role === 'lead');
          if (leadMember) {
            let lead = normalizedLeads.find(l => l.id === leadMember.id || l.uuid === leadMember.id);
            if (!lead) {
              lead = { id: leadMember.id, uuid: leadMember.id, name: leadMember.name, email: leadMember.email };
              setLeads(prev => [...prev, lead]);
            }
            setTeamLead(lead);
          }

          // Members
          const memberIds = team.members?.filter(m => m.role === 'member').map(m => m.id) || [];
          let members = normalizedUsers.filter(u => memberIds.includes(u.id || u.uuid));
          const missing = team.members
            .filter(m => m.role === 'member' && !members.some(u => u.id === m.id || u.uuid === m.id))
            .map(m => ({ id: m.id, uuid: m.id, name: m.name, email: m.email }));
          if (missing.length > 0) {
            setUsers(prev => [...prev, ...missing]);
            members = [...members, ...missing];
          }
          setSelectedUsers(members);
        }
      } catch (err) {
        toast.error('Failed to load data');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [teamId, mode]);

  // === VALIDATION & SUBMIT ===
  const validate = () => {
    const newErrors = { teamName: '', teamLead: '', status: '' };

    if (!teamName.trim()) newErrors.teamName = 'Team Name is required';
    if (!teamLead) newErrors.teamLead = 'Team Lead is required';
    if (!status) newErrors.status = 'Status is required';

    setErrors(newErrors);
    return Object.values(newErrors).every(e => e === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix all errors');
      return;
    }

    setIsSubmitting(true);
    setIsFormDisabled(true);

    const payload = {
      name: teamName.trim(),
      status_id: status,
      members: [
        { id: teamLead.uuid || teamLead.id, role: 'lead' },
        ...selectedUsers.map(u => ({ id: u.uuid || u.id, role: 'member' })),
      ],
    };

    try {
      let res;
      if (mode === 'edit' && teamId) {
        res = await dispatch(updateTeam({ teamId, teamData: payload })).unwrap();
      } else {
        res = await dispatch(createTeam(payload)).unwrap();
      }

      if (res?.data ?? res) {
        toast.success(`Team "${teamName}" ${mode === 'edit' ? 'updated' : 'created'}!`);
        navigate(TEAMS_LISTS_PATH);
      } else {
        throw new Error('Invalid response');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
      setIsFormDisabled(mode === 'view');
    }
  };

  const handleBack = () => navigate(TEAMS_LISTS_PATH);

  if (isLoading) {
    return (
      <ThemeProvider theme={revivePayTheme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', gap: 2 }}>
          <CircularProgress color="primary" />
          <Typography>Loading...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={revivePayTheme}>
      <Box sx={{ flexGrow: 1, p: 4, bgcolor: '#fff', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" fontWeight="bold">
            {mode === 'edit' ? 'Edit Team' : mode === 'view' ? 'View Team' : 'Create New Team'}
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, maxWidth: 900, mx: 'auto' }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>

              {/* Team Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                
                  label="Team Name"
                  value={teamName}
                  onChange={(e) => {
                    setTeamName(e.target.value);
                    if (e.target.value.trim()) setErrors(prev => ({ ...prev, teamName: '' }));
                  }}
                  error={!!errors.teamName}
                  helperText={errors.teamName}
                  disabled={isFormDisabled}
                  placeholder="e.g., Development Squad A"
                />
              </Grid>

              {/* Team Lead */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={leads}
                  getOptionLabel={(o) => o.name || ''}
                  value={teamLead}
                  onChange={(e, v) => {
                    setTeamLead(v);
                    if (v) setErrors(prev => ({ ...prev, teamLead: '' }));
                  }}
                  isOptionEqualToValue={(o, v) => o.id === v.id || o.uuid === v.uuid}
                  disabled={isFormDisabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team Lead"
                    
                      error={!!errors.teamLead}
                      helperText={errors.teamLead}
                    />
                  )}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.status} disabled={isFormDisabled}>
                  <InputLabel>Team Status</InputLabel>
                  <Select
                    value={status}
                    label="Team Status"
                    onChange={(e) => {
                      setStatus(e.target.value);
                      if (e.target.value) setErrors(prev => ({ ...prev, status: '' }));
                    }}
                  >
                    {statuses.length > 0 ? (
                      statuses.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No statuses</MenuItem>
                    )}
                  </Select>
                  {errors.status && (
                    <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                      {errors.status}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Team Members */}
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={users}
                  getOptionLabel={(u) => `${u.name} (${u.email})`}
                  value={selectedUsers}
                  onChange={(e, v) => setSelectedUsers(v)}
                  isOptionEqualToValue={(o, v) => o.id === v.id || o.uuid === v.uuid}
                  disableCloseOnSelect
                  disabled={isFormDisabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team Members (Optional)"
                      placeholder={selectedUsers.length === 0 ? 'Search users...' : ''}
                    />
                  )}
                  renderTags={(tags, getTagProps) =>
                    tags.map((opt, i) => (
                      <Chip
                        key={opt.uuid || opt.id}
                        label={opt.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        {...getTagProps({ i })}
                      />
                    ))
                  }
                />
              </Grid>

              {/* Submit */}
              {mode !== 'view' && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<SaveIcon />}
                    disabled={isSubmitting}
                    sx={{ py: 1.5, borderRadius: 2 }}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : mode === 'edit' ? 'Update Team' : 'Create Team'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default CreateTeamForm;